import React from 'react';
import { useFormikContext } from 'formik';
import { Box, Divider, FormControlLabel, Grid, IconButton, Switch, Typography, styled } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import DeleteIcon from '@mui/icons-material/Delete';

import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';
import { isDefined } from 'lib/typeguards';
import { Option } from 'lib/types';
import { LoyaltyScope } from 'components/loyalty/enums';
import { ExpandedMatchCodesInputModal } from 'components/adjustment-schemes/modals/ExpandedMatchCodesInputModal';
import { AwardRedemptionFullFormData } from './PerkRedemptionForm';

const StyledTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.spacing(2),
    fontWeight: theme.typography.fontWeightMedium
}));

const StyledSelectFormField = styled(SelectFormField)(() => ({
    width: 'auto'
}));

const scopeOptionsLabels: Record<string, string> = {
    ITEM: 'Menu Items',
    GROUP: 'Menu Groups',
    CHILD_ITEM: 'Modifier Items',
    ITEM_AND_CHILD_ITEM: 'Menu and Modifier Items',
    BASKET: 'Basket',
    QUALIFIER: 'Qualifier',
    MENU_ITEM: 'Menu Items'
};

interface PerkBasketQualificationSectionProps {
    currencySymbol?: string;
    isPseudoCurrency: boolean;
    discountValueString: string;
    basketScopeOptions: Option[];
    toggledFields?: string[];
    onToggleChange?: (callback: (state: string[]) => string[]) => void;
}

export const PerkBasketQualificationSection: React.FC<PerkBasketQualificationSectionProps> = ({
    currencySymbol,
    isPseudoCurrency,
    discountValueString,
    basketScopeOptions,
    toggledFields,
    onToggleChange
}) => {
    const { values, setFieldValue, errors } = useFormikContext<AwardRedemptionFullFormData>();
    const [showBasketExpandedMatchCodeInput, setBasketShowExpandedMatchCodeInput] =
        React.useState<boolean>(false);

    const showMatchCodesSection = React.useMemo(
        () =>
            isDefined(values.basketQualification?.scope) &&
            values.basketQualification.scope !== LoyaltyScope.BASKET,
        [values.basketQualification?.scope]
    );

    const showQualificationFields = React.useMemo(
        () =>
            isDefined(values.basketQualification?.matchCode) ||
            (isDefined(values.basketQualification?.scope) &&
                values.basketQualification.scope === LoyaltyScope.BASKET),
        [values.basketQualification?.matchCode, values.basketQualification?.scope]
    );

    const maxCountError = React.useMemo(
        () => errors.basketQualification?.maxCount,
        [errors.basketQualification]
    );

    const minimumItemsError = React.useMemo(
        () => errors.basketQualification?.minimumItems,
        [errors.basketQualification]
    );

    const minimumValueError = React.useMemo(
        () => errors.basketQualification?.minimumValue,
        [errors.basketQualification]
    );

    const handleBasketScopeSelect = React.useCallback(
        (value: any) => {
            if (value === LoyaltyScope.BASKET) {
                setFieldValue('basketQualification.matchCode', undefined);
            }
        },
        [setFieldValue]
    );

    const handleToggleField = React.useCallback(
        (field: string) => () => {
            if (onToggleChange) {
                onToggleChange(state => {
                    if (state.includes(field)) {
                        setFieldValue(field, undefined);
                        return state.filter(val => val !== field);
                    }

                    return [...state, field];
                });
            }
        },
        [onToggleChange, setFieldValue]
    );

    const handleBasketExpandInput = React.useCallback(() => {
        setBasketShowExpandedMatchCodeInput(true);
    }, []);

    const handleCloseBasketExpandedInput = React.useCallback(() => {
        setBasketShowExpandedMatchCodeInput(false);
    }, []);

    const handleConfirmBasketMatchCodeEdit = React.useCallback(
        (editedMatchCodes: string) => {
            setFieldValue('basketQualification.matchCode', editedMatchCodes);
            setBasketShowExpandedMatchCodeInput(false);
        },
        [setFieldValue]
    );

    return (
        <>
            <Grid item xs={12}>
                <Divider />
            </Grid>
            <Grid item xs={12}>
                <Box display="flex" flexDirection="column" marginBottom={1.5}>
                    <StyledTitle>4.2 Setting up the basket qualification</StyledTitle>
                    <Typography variant="caption">
                        This section controls what items you must have in your basket, alongside the
                        discountable items to be able to qualify for the award
                    </Typography>
                </Box>
                <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                        <SwitchFormField
                            name="basketQualification.disableBasketQualification"
                            label={<MuiFormLabel>Do not set basket qualification</MuiFormLabel>}
                        />
                    </Grid>
                    {!values.basketQualification?.disableBasketQualification && (
                        <>
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center" columnGap="4px">
                                    <Typography variant="body2">
                                        {' '}
                                        {discountValueString} will only be redeemable if you have
                                    </Typography>
                                    <Box>
                                        <StyledSelectFormField
                                            options={basketScopeOptions}
                                            name="basketQualification.scope"
                                            onSelect={handleBasketScopeSelect}
                                        />
                                    </Box>
                                    <Typography variant="body2">in the basket</Typography>
                                </Box>
                            </Grid>

                            {showMatchCodesSection && (
                                <Grid item xs={12}>
                                    <Box display="inline" lineHeight="2">
                                        <Typography variant="body2" display="inline">
                                            Assign the perk code
                                        </Typography>
                                        <Box
                                            width={isPseudoCurrency ? 200 : 300}
                                            display="inline-flex"
                                            justifyItems="center"
                                            gap={1}
                                            paddingX={1}
                                        >
                                            <Box display="flex" flex={1} justifyItems="center">
                                                <span>&ldquo;</span>
                                                <TextFormField
                                                    name="basketQualification.matchCode"
                                                    placeholder={
                                                        isPseudoCurrency
                                                            ? 'e.g. buy 2 mains'
                                                            : 'e.g. buy 2 mains, buy 2 coffees'
                                                    }
                                                    multiline={!isPseudoCurrency}
                                                    maxRows={4}
                                                />
                                                <span>&rdquo;</span>
                                            </Box>
                                            <Box>
                                                <IconButton size="small" onClick={handleBasketExpandInput}>
                                                    <OpenInFullIcon />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                        <Typography variant="body2" display="inline">
                                            to the{' '}
                                            {scopeOptionsLabels[values.basketQualification.scope as string]}{' '}
                                            in your menu via the menu manager page under the Perks column in
                                            the table
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}

                            {showQualificationFields && (
                                <>
                                    <Grid item xs={12}>
                                        {toggledFields?.includes('basketQualification.minimumValue') ? (
                                            <Box display="flex" flexDirection="column" gap={1}>
                                                <Box display="flex">
                                                    <Box display="flex" alignItems="center" columnGap="4px">
                                                        <Typography variant="body2">
                                                            The{' '}
                                                            {values.basketQualification.scope ===
                                                            LoyaltyScope.BASKET
                                                                ? 'basket'
                                                                : 'product(s)'}{' '}
                                                            must have a minimum cost of {currencySymbol}
                                                        </Typography>
                                                        <Box width={44}>
                                                            <TextFormField
                                                                name="basketQualification.minimumValue"
                                                                type="number"
                                                                showHelperText={false}
                                                            />
                                                        </Box>
                                                    </Box>
                                                    <IconButton
                                                        onClick={handleToggleField(
                                                            'basketQualification.minimumValue'
                                                        )}
                                                        aria-label="delete"
                                                        size="small"
                                                    >
                                                        <DeleteIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Box>
                                                {minimumValueError && (
                                                    <Typography variant="body2" color="error">
                                                        {minimumValueError}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        onChange={handleToggleField(
                                                            'basketQualification.minimumValue'
                                                        )}
                                                    />
                                                }
                                                label="Set a minimum value on the product or basket"
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12}>
                                        {toggledFields?.includes('basketQualification.minimumItems') ? (
                                            <Box display="flex" flexDirection="column" gap={1}>
                                                <Box display="flex" alignItems="center">
                                                    <Box display="flex" alignItems="center" columnGap="4px">
                                                        <Typography variant="body2">
                                                            There must be a minimum of
                                                        </Typography>
                                                        <Box width={44}>
                                                            <TextFormField
                                                                name="basketQualification.minimumItems"
                                                                type="number"
                                                                showHelperText={false}
                                                            />
                                                        </Box>
                                                        <Typography variant="body2">
                                                            product(s) in the basket
                                                        </Typography>
                                                    </Box>
                                                    <IconButton
                                                        onClick={handleToggleField(
                                                            'basketQualification.minimumItems'
                                                        )}
                                                        aria-label="delete"
                                                        size="small"
                                                    >
                                                        <DeleteIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Box>
                                                {minimumItemsError && (
                                                    <Typography variant="body2" color="error">
                                                        {minimumItemsError}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        onChange={handleToggleField(
                                                            'basketQualification.minimumItems'
                                                        )}
                                                    />
                                                }
                                                label="Set a minimum number of products"
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12}>
                                        {toggledFields?.includes('basketQualification.maxCount') ? (
                                            <Box display="flex" flexDirection="column" gap={1}>
                                                <Box display="flex" alignItems="center">
                                                    <Box display="flex" alignItems="center" columnGap="4px">
                                                        <Typography variant="body2">
                                                            The perk can be applied
                                                        </Typography>
                                                        <Box width={44}>
                                                            <TextFormField
                                                                name="basketQualification.maxCount"
                                                                type="number"
                                                                showHelperText={false}
                                                            />
                                                        </Box>
                                                        <Typography variant="body2">
                                                            time(s) in a single basket for each matching
                                                            basket qualifier (if a qualifier has been set)
                                                        </Typography>
                                                    </Box>
                                                    <IconButton
                                                        onClick={handleToggleField(
                                                            'basketQualification.maxCount'
                                                        )}
                                                        aria-label="delete"
                                                        size="small"
                                                    >
                                                        <DeleteIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Box>
                                                {maxCountError && (
                                                    <Typography variant="body2" color="error">
                                                        {maxCountError}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        onChange={handleToggleField(
                                                            'basketQualification.maxCount'
                                                        )}
                                                    />
                                                }
                                                label="Set a maximum count"
                                            />
                                        )}
                                    </Grid>
                                </>
                            )}
                        </>
                    )}
                </Grid>
            </Grid>
            {showBasketExpandedMatchCodeInput && (
                <ExpandedMatchCodesInputModal
                    placeholder={isPseudoCurrency ? 'e.g. buy 2 mains' : 'e.g. buy 2 mains, buy 2 coffees'}
                    matchCodes={values.basketQualification.matchCode as string | undefined}
                    open={showBasketExpandedMatchCodeInput}
                    onConfirm={handleConfirmBasketMatchCodeEdit}
                    onClose={handleCloseBasketExpandedInput}
                />
            )}
        </>
    );
};
